import {useMemo} from "react";
import {FieldChange} from "../../utils/recordDiff";
import {SectionKey} from "../../utils/branchDiff";
import {useIntegrator} from "../../integrator.context";
import {classifyResolve, collectCurrentKnownIds} from "../../utils/resolveDifference";
import {ResolveFieldFromOtherCommand} from "../../commands/resolveFieldFromOther.command";
import ActionButton from "./actionButton";

interface RecordDiffViewProps {
    fieldChanges: FieldChange[];
    sectionKey: SectionKey;
    identity: string;
}

function formatScalar(v: unknown): string {
    // Key absent on this side (e.g. values.attackBonus exists in Other but
    // not Current) — distinguished from `null` so the user can see "this
    // parameter didn't exist" vs "this parameter exists but is null".
    if (v === undefined) return 'missing';
    if (v === null) return 'null';
    if (typeof v === 'string') return v === '' ? '""' : v;
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
}

function RecordDiffView(props: RecordDiffViewProps) {
    const {state, executeCommand} = useIntegrator();

    const currentKnownIds = useMemo(() => collectCurrentKnownIds(state), [state]);
    // Reuse the "any uid that appears anywhere" set to decide whether a raw
    // string is a uid worth checking. Cheap; rebuilt on state change.
    const knownIdsBoth = useMemo(() => new Set<string>(currentKnownIds), [currentKnownIds]);

    return (
        <div className={'layout vertical'} style={{paddingLeft: 24, marginTop: 4, marginBottom: 4, gap: 2}}>
            {props.fieldChanges.map((fc, i) => {
                const path = fc.displayPath ?? fc.path.join('.');
                const cls = classifyResolve(fc, props.sectionKey, props.identity, currentKnownIds, knownIdsBoth);
                const resolveButton = (
                    <div style={{alignSelf: 'flex-start'}}>
                        <ActionButton
                            variant={'pull'}
                            small
                            label={'Pull'}
                            disabled={!cls.resolvable}
                            title={cls.resolvable ? "Resolve: pull this from Other into Current" : (cls.reason ?? '')}
                            onClick={() => executeCommand(new ResolveFieldFromOtherCommand(cls.actions))}/>
                    </div>
                );

                if (fc.kind === 'set' || fc.kind === 'objset') {
                    const added = fc.displayAdded ?? fc.added;
                    const removed = fc.displayRemoved ?? fc.removed;
                    return (
                        <div key={i} className={'layout horizontal'}
                             style={{borderLeft: 'solid 2px var(--overlay-color)', paddingLeft: 8, gap: 8, alignItems: 'flex-start'}}>
                            <div className={'layout vertical flex-grow'}>
                                <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12}}>{path}</p>
                                {added && added.length > 0 &&
                                    <p style={{color: '#66cc66'}}>+ {added.join(', ')}</p>}
                                {removed && removed.length > 0 &&
                                    <p style={{color: '#ff6666'}}>- {removed.join(', ')}</p>}
                            </div>
                            {resolveButton}
                        </div>
                    );
                }
                return (
                    <div key={i} className={'layout horizontal gap'}
                         style={{borderLeft: 'solid 2px var(--overlay-color)', paddingLeft: 8, gap: 8}}>
                        <p style={{color: 'rgba(255,255,255,0.66)', fontSize: 12, minWidth: 200}}>{path}</p>
                        <p style={{color: '#ff6666'}}>{formatScalar(fc.displayCurrent ?? fc.current)}</p>
                        <span className={'fa-solid fa-arrow-right'} style={{color: 'rgba(255,255,255,0.66)', fontSize: 11, alignSelf: 'center'}}/>
                        <p style={{color: '#66cc66', flexGrow: 1}}>{formatScalar(fc.displayTarget ?? fc.target)}</p>
                        {resolveButton}
                    </div>
                );
            })}
        </div>
    );
}

export default RecordDiffView;
